import { useEffect, useState } from 'react'
import Modal from './Modal'
import Boton from './Boton'
import api from '../api/client'
import { fmtMonto } from '../utils/fecha'
import { colors, font, text, space, radius } from '../design/tokens'

/*
 * Traer de Presea la factura y el remito hechos A MANO sobre la nota de venta
 * de esta NV (Martin, 20 oct 2026 — caso Factura de Crédito Electrónica, que
 * el WS no emite). LGR busca por DETALLE_NOTA con el número de nota de Presea,
 * muestra lo que hay y, con el OK, la NV pasa a procesada con su remito de
 * logística (el tipo T80/T90 es el que informa Presea). Las validaciones
 * viven en el backend (VincularComprobantesPreseaService): acá solo se pinta.
 */
const TRANSPORTE = { 0: 'virtual (T0)', 80: 'T80 — varias direcciones (planilla de envío)', 90: 'T90 — entrega única' }

export default function ModalComprobantesPresea({ nv, onClose, onVinculado }) {
  const [previa, setPrevia] = useState(null)
  const [error, setError] = useState('')
  const [vinculando, setVinculando] = useState(false)

  useEffect(() => {
    api.get(`/notas-venta/${nv.id}/comprobantes-presea`)
      .then(({ data }) => setPrevia(data))
      .catch((e) => setError(e.response?.data?.message || 'No se pudo consultar Presea.'))
  }, [nv.id])

  const vincular = async () => {
    setVinculando(true); setError('')
    try {
      const { data } = await api.post(`/notas-venta/${nv.id}/comprobantes-presea`)
      onVinculado(data)
    } catch (e) {
      const errs = e.response?.data?.errors
      setError(errs ? Object.values(errs).flat().join(' · ') : (e.response?.data?.message || 'No se pudo vincular.'))
    } finally { setVinculando(false) }
  }

  const ok = previa && previa.errores.length === 0

  return (
    <Modal title="Traer comprobantes de Presea" onClose={onClose} maxWidth={640}>
      <p style={st.ayuda}>
        Para cuando esta venta se facturó y despachó <b>a mano en Presea</b> (por ejemplo, Factura de Crédito Electrónica).
        LGR busca los comprobantes de la nota <span style={st.mono}>{nv.numero_nv_presea}</span> y los vincula a esta NV.
      </p>

      {!previa && !error && <p style={st.cargando}>Consultando Presea…</p>}

      {previa && (
        <div style={st.lista}>
          {previa.factura && <Fila etiqueta="Factura" f={previa.factura} detalle={`IVA ${fmtMonto(previa.factura.iva)} · Percepciones ${fmtMonto(previa.factura.percep_iva)}`} />}
          {previa.remito && <Fila etiqueta="Remito" f={previa.remito} detalle={TRANSPORTE[previa.transporte] ? `Entra a logística como ${TRANSPORTE[previa.transporte]}` : null} sinImporte />}
          {previa.recibos.map(r => <Fila key={r.numero} etiqueta="Recibo" f={r} detalle="Ya está hecho en Presea: los cobros de la NV no se vuelven a enviar." />)}
          {previa.otros.map(r => <Fila key={r.numero} etiqueta="Otro" f={r} />)}
          {previa.anulados.map(r => <Fila key={r.numero} etiqueta="Anulado" f={r} apagado sinImporte />)}
        </div>
      )}

      {previa?.errores.length > 0 && (
        <div style={st.errorBox}>
          <b>No se puede vincular:</b>
          <ul style={st.ul}>{previa.errores.map(e => <li key={e}>{e}</li>)}</ul>
        </div>
      )}
      {error && <div style={st.errorBox}>{error}</div>}

      <div style={st.actions}>
        <Boton variante="secondary" onClick={onClose} disabled={vinculando}>Cancelar</Boton>
        <Boton variante="primary" onClick={vincular} disabled={!ok || vinculando}>
          {vinculando ? 'Vinculando…' : '📥 Vincular a esta NV'}
        </Boton>
      </div>
    </Modal>
  )
}

function Fila({ etiqueta, f, detalle, apagado, sinImporte }) {
  return (
    <div style={{ ...st.fila, opacity: apagado ? 0.55 : 1 }}>
      <div style={st.filaTop}>
        <span style={st.etiqueta}>{etiqueta}</span>
        <span style={st.form}>{f.formulario}</span>
        <span style={st.mono}>{f.numero}</span>
        <span style={st.fecha}>{f.fecha}</span>
        {!sinImporte && <span style={st.importe}>{fmtMonto(f.importe)}</span>}
      </div>
      {detalle && <p style={st.detalle}>{detalle}</p>}
    </div>
  )
}

const st = {
  ayuda: { margin: 0, fontSize: text.body, color: colors.textSub, lineHeight: 1.5 },
  cargando: { margin: `${space.lg}px 0 0`, fontSize: text.body, color: colors.textMeta },
  lista: { marginTop: space.lg, border: `1px solid ${colors.border}`, borderRadius: radius.md, background: colors.bgSoft },
  fila: { padding: `${space.md}px ${space.lg}px`, borderBottom: `1px solid ${colors.borderSoft}` },
  filaTop: { display: 'flex', alignItems: 'baseline', gap: space.md, flexWrap: 'wrap' },
  etiqueta: { fontSize: text.meta, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', color: colors.textSec, minWidth: 64 },
  form: { fontSize: text.body, color: colors.text, fontWeight: 600 },
  mono: { fontFamily: font.mono, fontVariantNumeric: 'tabular-nums', fontSize: text.body, color: colors.text },
  fecha: { fontFamily: font.mono, fontSize: text.sec, color: colors.textSec },
  importe: { marginLeft: 'auto', fontFamily: font.mono, fontVariantNumeric: 'tabular-nums', fontSize: text.body, fontWeight: 600, color: colors.text },
  detalle: { margin: `${space.xs}px 0 0 72px`, fontSize: text.sec, color: colors.textSec },
  errorBox: { marginTop: space.lg, background: colors.errBg, color: colors.err, padding: `${space.md}px ${space.lg}px`, borderRadius: radius.sm, fontSize: text.body },
  ul: { margin: `${space.xs}px 0 0`, paddingLeft: space['2xl'] },
  actions: { display: 'flex', justifyContent: 'flex-end', gap: space.md, marginTop: space.xl },
}
